import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Info,
} from 'lucide-react';

const FundPasswordModal = ({
  isOpen,
  onClose,
  fundPasswordData,
  loading,
  onUpdate,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updating, setUpdating] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate numeric only
    if (!/^\d+$/.test(newPassword)) {
      toast.error('Password must contain only numbers');
      return;
    }

    // Validate exactly 6 digits
    if (newPassword.length !== 6) {
      toast.error('Password must be exactly 6 digits');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setUpdating(true);
    const result = await onUpdate(newPassword);
    setUpdating(false);

    if (result.success) {
      toast.success('Fund password updated successfully! 🔐', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setNewPassword('');
      setConfirmPassword('');
      // Auto-close modal after successful update
      setTimeout(() => {
        onClose();
      }, 1500);
    } else {
      toast.error(result.message || 'Failed to update fund password');
    }
  };

  return (
    <div
      className='modal d-block'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className='modal-dialog modal-dialog-centered modal-lg'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-content border-0 shadow-lg'>
          {/* Header */}
          <div
            className='modal-header text-white border-0'
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <h5 className='modal-title d-flex align-items-center gap-2'>
              <Lock size={24} />
              Fund Password Management
            </h5>
            <button
              type='button'
              className='btn-close btn-close-white'
              onClick={onClose}
            ></button>
          </div>

          {/* Body */}
          <div className='modal-body p-4'>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <p className='mt-3 text-muted'>
                  Loading fund password details...
                </p>
              </div>
            ) : fundPasswordData ? (
              <>
                {/* User Info Card */}
                <div className='card bg-light border-0 mb-4'>
                  <div className='card-body'>
                    <h6 className='card-title fw-bold mb-3'>
                      User Information
                    </h6>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>UID</small>
                        <strong>{fundPasswordData.uid}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Email</small>
                        <strong>{fundPasswordData.email}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Full Name</small>
                        <strong>{fundPasswordData.fullName || 'N/A'}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Status</small>
                        <span
                          className={`badge ${
                            fundPasswordData.status === 'ACTIVE'
                              ? 'bg-success'
                              : 'bg-danger'
                          }`}
                        >
                          {fundPasswordData.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Password Status */}
                <div className='mb-4'>
                  <h6 className='fw-bold mb-3'>Current Password Status</h6>

                  {fundPasswordData.isLegacyPassword ? (
                    <div className='alert alert-warning d-flex align-items-start gap-2'>
                      <AlertCircle size={20} className='mt-1 flex-shrink-0' />
                      <div>
                        <strong>Legacy Password Detected</strong>
                        <p className='mb-0 small mt-1'>
                          {fundPasswordData.legacyNote}
                        </p>
                      </div>
                    </div>
                  ) : fundPasswordData.transactionPassword ? (
                    <div className='alert alert-success d-flex align-items-start gap-2'>
                      <CheckCircle size={20} className='mt-1 flex-shrink-0' />
                      <div>
                        <strong>Current Password</strong>
                        <p className='mb-0 mt-1'>
                          <code className='bg-white px-2 py-1 rounded'>
                            {fundPasswordData.transactionPassword}
                          </code>
                        </p>
                        <small className='text-muted d-block mt-2'>
                          Last updated:{' '}
                          {new Date(
                            fundPasswordData.transactionPasswordSetAt
                          ).toLocaleString()}
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div className='alert alert-info d-flex align-items-start gap-2'>
                      <AlertCircle size={20} className='mt-1 flex-shrink-0' />
                      <div>
                        <strong>No Password Set</strong>
                        <p className='mb-0 small mt-1'>
                          This user has not set a fund password yet.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Update Password Form */}
                <div className='card border-primary'>
                  <div className='card-header bg-primary bg-opacity-10 border-primary'>
                    <h6 className='mb-0 fw-bold text-primary'>
                      Update Fund Password
                    </h6>
                  </div>
                  <div className='card-body'>
                    <form onSubmit={handleSubmit}>
                      <div className='mb-3'>
                        <label className='form-label fw-semibold'>
                          New Password (6 digits only){' '}
                          <span className='text-danger'>*</span>
                        </label>
                        <div className='input-group'>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            className='form-control'
                            placeholder='Enter 6-digit number (e.g., 123456)'
                            value={newPassword}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 6) {
                                setNewPassword(value);
                              }
                            }}
                            disabled={updating}
                            maxLength={6}
                            inputMode='numeric'
                            pattern='\d*'
                          />
                          <button
                            className='btn btn-outline-secondary'
                            type='button'
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        </div>
                        <small className='text-muted'>
                          Only numbers allowed (exactly 6 digits)
                        </small>
                      </div>

                      <div className='mb-4'>
                        <label className='form-label fw-semibold'>
                          Confirm Password{' '}
                          <span className='text-danger'>*</span>
                        </label>
                        <div className='input-group'>
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            className='form-control'
                            placeholder='Confirm 6-digit number'
                            value={confirmPassword}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 6) {
                                setConfirmPassword(value);
                              }
                            }}
                            disabled={updating}
                            maxLength={6}
                            inputMode='numeric'
                            pattern='\d*'
                          />
                          <button
                            className='btn btn-outline-secondary'
                            type='button'
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className='d-flex gap-2 justify-content-end'>
                        <button
                          type='button'
                          className='btn btn-secondary'
                          onClick={onClose}
                          disabled={updating}
                        >
                          Cancel
                        </button>
                        <button
                          type='submit'
                          className='btn btn-primary'
                          disabled={updating}
                        >
                          {updating ? (
                            <>
                              <span className='spinner-border spinner-border-sm me-2' />
                              Updating...
                            </>
                          ) : (
                            <>
                              <Lock size={18} className='me-2' />
                              Update Password
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </>
            ) : (
              <div className='text-center py-5'>
                <div className='mb-4'>
                  <Info
                    size={64}
                    className='text-primary mb-3'
                    style={{ opacity: 0.7 }}
                  />
                </div>
                <h5 className='fw-bold mb-2'>No Fund Password Set</h5>
                <p className='text-muted mb-0'>
                  This user has not configured a fund password yet.
                </p>
                <p className='text-muted small'>
                  You can set up a new fund password using the form below.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundPasswordModal;
